/**
 * Single home for "release this WZ from stock" — the DRAFT/CONFIRMED →
 * RELEASED transition that consumes the FIFO batches, freezes the document's
 * COGS. Later edits use the explicit audited correction command.
 *
 * Two entry points share it, and must stay identical in what they call, what
 * they say, and how they fail:
 *   - the save-time choice (askWarehouseSaveIntent → POST) on WZ create/edit;
 *   - the "Rozlicz wydanie" button on an already-saved draft.
 *
 * It also owns the WZ_RELEASE_EXCEEDS_ORDERED_QUANTITY handshake. Over-delivery
 * is a WARNING, never a hard block (real logistics ships more than was ordered)
 * — the backend has no i18n, so it answers with an English message + `code` +
 * structured `lines`, which this module renders as a Polish confirmation naming
 * each affected line before re-sending with the operator's acknowledgement.
 */

import React from 'react';
import type { ReactNode } from 'react';

import api from '../../services/api';
import { getWZErrorMessage, isBlockingWZError } from './wzErrorMessages';
import { formatWarehouseQuantity } from './warehouseQuantityConversion';
import { formatUnit } from '../../utils/units';
import type { FeedbackApi } from '../../utils/feedback';

interface ExceedsOrderedQuantityLine {
  productName: string;
  unit: string;
  orderedQuantity: number;
  alreadyReleasedQuantity: number;
  addingQuantity: number;
  projectedQuantity: number;
}

interface UnitMismatchLine {
  productName: string;
  unit: string;
  orderUnits: string[];
  quantity: number;
  source: 'releasedSibling' | 'thisDocument';
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface ModalLike {
  confirm: (config: {
    title: ReactNode;
    content?: ReactNode;
    width?: number;
    okText?: ReactNode;
    cancelText?: ReactNode;
    okType?: 'primary' | 'danger' | 'default' | 'dashed' | 'link' | 'text';
    onOk?: () => void;
    onCancel?: () => void;
  }) => unknown;
}

export interface ReleaseWZDocumentParams {
  wzId: string;
  /** Operator-facing document number, so the toast never reads as a bare "Wydano". */
  wzNumber?: string | null;
  /** `modal` from antd's `App.useApp()` — never the static `Modal.confirm`. */
  modal: ModalLike;
  t: TranslateFn;
  language: string;
  feedback: FeedbackApi;
}

interface ReleaseOptions {
  confirmExceedsOrderedQuantity?: boolean;
  confirmationFingerprint?: string;
}

export async function releaseWZDocument(
  params: ReleaseWZDocumentParams,
  options?: ReleaseOptions,
): Promise<boolean> {
  const { wzId, wzNumber, modal, t, language, feedback } = params;

  try {
    await api.put(`/wz/${wzId}/status`, {
      status: 'RELEASED',
      ...(options?.confirmExceedsOrderedQuantity ? { confirmExceedsOrderedQuantity: true } : {}),
      ...(options?.confirmationFingerprint ? { confirmationFingerprint: options.confirmationFingerprint } : {}),
    });
    feedback.success(wzNumber
      ? t('wz:success.statusUpdated', { wzNumber })
      : t('wz:success.statusUpdatedFallback'));
    return true;
  } catch (error: any) {
    const data = error?.response?.data;
    if (data?.code !== 'WZ_RELEASE_EXCEEDS_ORDERED_QUANTITY') {
      // Every remaining release failure — no stock, unlinked lines, illegal
      // transition — is a state the operator must fix first, so the sentence
      // stays on screen instead of blinking away.
      feedback.error(getWZErrorMessage(error, t, 'wz:error.statusUpdateFailed'), {
        sticky: isBlockingWZError(error),
      });
      return false;
    }

    const confirmed = await confirmWZQuantityWarning(data, modal, t, language);

    if (!confirmed) {
      return false;
    }

    return releaseWZDocument(params, {
      confirmExceedsOrderedQuantity: true,
      confirmationFingerprint: data.confirmationFingerprint,
    });
  }
}

export async function confirmWZQuantityWarning(
  data: any, modal: ReleaseWZDocumentParams['modal'], t: ReleaseWZDocumentParams['t'], language: string,
): Promise<boolean> {
    const lines: ExceedsOrderedQuantityLine[] = Array.isArray(data.lines) ? data.lines : [];
    const unitMismatchLines: UnitMismatchLine[] = Array.isArray(data.unitMismatchLines)
      ? data.unitMismatchLines
      : [];

    return new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('wz:confirm.exceedsOrderedQuantityTitle'),
        width: 560,
        content: (
          <div>
            {lines.length > 0 && <p>{t('wz:confirm.exceedsOrderedQuantityIntro')}</p>}
            <ul>
              {lines.map((line, index) => (
                <li key={`${line.productName}-${index}`}>
                  {t('wz:confirm.exceedsOrderedQuantityLine', {
                    productName: line.productName,
                    ordered: formatWarehouseQuantity(line.orderedQuantity, line.unit, language),
                    alreadyReleased: formatWarehouseQuantity(line.alreadyReleasedQuantity, line.unit, language),
                    adding: formatWarehouseQuantity(line.addingQuantity, line.unit, language),
                    projected: formatWarehouseQuantity(line.projectedQuantity, line.unit, language),
                  })}
                </li>
              ))}
              {unitMismatchLines.map((line, index) => (
                <li key={`unit-${line.productName}-${index}`}>
                  {t('wz:confirm.unitMismatchLine', {
                    productName: line.productName,
                    quantity: formatWarehouseQuantity(line.quantity, line.unit, language),
                    orderUnits: line.orderUnits.map((unit) => formatUnit(unit, language)).join(', '),
                    source: t(`wz:confirm.unitMismatchSource.${line.source}`),
                  })}
                </li>
              ))}
            </ul>
            <p>{t('wz:confirm.exceedsOrderedQuantityFooter')}</p>
          </div>
        ),
        okText: t('wz:confirm.exceedsOrderedQuantityConfirm'),
        cancelText: t('common:cancel'),
        okType: 'danger',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

}
